import { useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Loader2, ClipboardCheck, Coins, Quote } from 'lucide-react';
import FileDropzone from '../components/features/FileDropzone';
import RubricBuilder, { defaultRubric } from '../components/features/RubricBuilder';
import { parseFile, ParsedFile } from '../services/fileParser';
import { gradeByRubric } from '../services/gradingService';
import { saveJob, uploadReportFile } from '../services/jobsService';
import { useAuth } from '../contexts/AuthContext';
import type { Rubric, RubricGradeResult } from '../types';

export default function RubricGrade() {
  const { user, addCredits } = useAuth();
  const [file, setFile] = useState<File | null>(null);
  const [parsed, setParsed] = useState<ParsedFile | null>(null);
  const [rubric, setRubric] = useState<Rubric>(defaultRubric());
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<RubricGradeResult | null>(null);

  const onFile = async (f: File) => {
    setFile(f); setParsed(null); setResult(null);
    try {
      const p = await parseFile(f);
      setParsed(p);
      toast.success(`Parsed ${p.wordCount.toLocaleString()} words`);
    } catch (e: any) { toast.error(e.message); setFile(null); }
  };

  const run = async () => {
    if (!parsed) return toast.error('Upload a report first');
    if (!rubric.criteria.length) return toast.error('Add at least one rubric criterion');
    if (!user || user.credits < 1) return toast.error('Need 1 credit to grade');
    setLoading(true);
    try {
      const r = await gradeByRubric(parsed, rubric);
      setResult(r);
      addCredits(-1);
      // Persist: fire-and-forget upload + job record.
      const jobId = crypto.randomUUID();
      const storagePath = file ? await uploadReportFile(file, jobId) : undefined;
      await saveJob({
        kind: 'rubric',
        fileName: parsed.fileName,
        storagePath,
        rubricName: r.rubricName,
        score: r.totalScore,
        grade: r.letterGrade,
      });
      toast.success('Rubric grading complete');
    } catch (e: any) { toast.error(e.message); } finally { setLoading(false); }
  };

  const importRubric = async (f: File) => {
    const text = await f.text();
    try {
      const parsed = JSON.parse(text);
      if (!parsed.criteria) throw new Error('Not a rubric JSON');
      setRubric(parsed);
      toast.success('Rubric imported');
    } catch (e: any) { toast.error('Could not parse rubric. Use JSON export format.'); }
  };

  const exportRubric = () => {
    const blob = new Blob([JSON.stringify(rubric, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${rubric.name.replace(/\s+/g, '_')}.rubric.json`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="container-wide py-10">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-display font-bold">Grade by rubric</h1>
          <p className="text-ink-600 mt-1">Define criteria and weights — we score the report against them.</p>
        </div>
        {user && (
          <span className="chip bg-brand-50 text-brand-700 border border-brand-100 px-3 py-1.5 text-sm">
            <Coins className="w-4 h-4 mr-1.5" /> {user.credits} credits
          </span>
        )}
      </div>

      <div className="mt-6 grid lg:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div>
            <h3 className="text-sm font-semibold text-ink-700 mb-2">1. Upload the report</h3>
            <FileDropzone onFile={onFile} selectedFile={file} onClear={() => { setFile(null); setParsed(null); setResult(null); }} />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-ink-700">2. Rubric</h3>
              <div className="flex gap-2">
                <label className="btn-ghost btn-sm cursor-pointer">
                  Import JSON
                  <input type="file" accept=".json" className="hidden"
                    onChange={(e) => e.target.files?.[0] && importRubric(e.target.files[0])} />
                </label>
                <button onClick={exportRubric} className="btn-ghost btn-sm">Export JSON</button>
              </div>
            </div>
            <RubricBuilder rubric={rubric} onChange={setRubric} />
          </div>

          <button onClick={run} disabled={loading || !parsed} className="btn-primary w-full">
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Scoring…</>
                     : <><ClipboardCheck className="w-4 h-4" /> Grade with this rubric (1 credit)</>}
          </button>
          {user && user.credits < 1 && (
            <p className="text-xs text-amber-700">
              Out of credits — <Link to="/pricing" className="underline">top up</Link>.
            </p>
          )}
        </div>

        <div>
          {!result && !loading && (
            <div className="card p-10 text-center text-ink-500 h-full grid place-items-center">
              <div>
                <ClipboardCheck className="w-10 h-10 mx-auto text-brand-400" />
                <p className="mt-3 text-sm">Your criterion-level scores will appear here.</p>
              </div>
            </div>
          )}
          {loading && (
            <div className="card p-10 text-center">
              <Loader2 className="w-8 h-8 text-brand-600 animate-spin mx-auto" />
              <p className="mt-3 text-sm text-ink-600">Matching rubric criteria to the report…</p>
            </div>
          )}
          {result && (
            <div className="card p-6 animate-fade-in">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs text-ink-500">Rubric</div>
                  <h2 className="font-display font-bold text-xl">{result.rubricName}</h2>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-display font-bold text-brand-700">{result.totalScore}</div>
                  <div className="text-sm text-emerald-600 font-medium">{result.letterGrade}</div>
                </div>
              </div>

              <p className="mt-4 text-sm text-ink-700">{result.overallFeedback}</p>

              <div className="mt-5 space-y-3">
                {result.criteriaResults.map((r) => {
                  const pct = Math.round((r.score / r.maxScore) * 100);
                  return (
                    <div key={r.criterionId} className="border border-ink-100 rounded-xl p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="font-medium">{r.criterionName}</div>
                          <div className="text-xs text-ink-500">Weight {r.weight}%</div>
                        </div>
                        <div className="text-right">
                          <div className="font-semibold text-ink-900">{r.score}<span className="text-ink-400 text-sm">/{r.maxScore}</span></div>
                          <div className="text-xs text-ink-500">{pct}%</div>
                        </div>
                      </div>
                      <div className="mt-2 h-1.5 bg-ink-100 rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-brand-500 to-fuchsia-500" style={{ width: `${pct}%` }} />
                      </div>
                      <p className="mt-2 text-sm text-ink-700">{r.feedback}</p>
                      {r.evidenceQuotes?.[0] && (
                        <div className="mt-2 p-2 bg-ink-50 rounded-lg text-xs text-ink-600 flex gap-2">
                          <Quote className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                          <span className="italic">"{r.evidenceQuotes[0]}"</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
